/**
 * Cliente HTTP simples para o backend SíndicoApp.
 *
 * Em desenvolvimento, Vite proxy encaminha /api/* para http://127.0.0.1:7423.
 * Em produção (Tauri ou bundle estático), o backend embarcado responde direto.
 */

// Em dev, "/api" é encaminhado pelo proxy do Vite ao backend (porta 7423).
// No app empacotado (Tauri/produção), o frontend é servido estaticamente e
// precisa chamar o backend direto — defina VITE_API_BASE no build, ex.:
//   VITE_API_BASE=http://127.0.0.1:7423/api
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

// ----- Sessão (token de login multiusuário) -----
// A sessão fica em sessionStorage (NÃO localStorage) de propósito: ela dura só
// enquanto a aba/app está aberta. Ao FECHAR e REABRIR a URL/PWA, o token some e
// o app pede login de novo — evita que o próximo a abrir o navegador caia na
// sessão de quem entrou antes (ex.: reabrir e já estar logado como outro morador).
// Um F5/recarregar no meio do uso mantém a sessão (sessionStorage sobrevive ao reload).
const TOKEN_KEY = "sindicoapp_token";
// Limpa qualquer token ANTIGO que tenha ficado salvo em localStorage (versões
// anteriores gravavam lá e mantinham o login para sempre).
try {
  if (typeof localStorage !== "undefined") localStorage.removeItem(TOKEN_KEY);
} catch {
  /* ignore */
}
let _token: string | null =
  (typeof sessionStorage !== "undefined" && sessionStorage.getItem(TOKEN_KEY)) || null;

export function setToken(token: string | null): void {
  _token = token;
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* sessionStorage pode falhar em modo restrito — token em memória basta */
  }
}

export function getToken(): string | null {
  return _token;
}

/** Monta uma URL absoluta para a API (para <img src>, links, window.open). */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

/**
 * Acrescenta o token de sessão à URL como `?token=…`. Necessário para mídia
 * carregada FORA do cliente HTTP — `<img src>`, `window.open`, downloads —
 * que não enviam o cabeçalho Authorization. Sem isto, essas chamadas caem no
 * login (401/403) desde que as rotas passaram a exigir usuário logado.
 */
function comToken(url: string): string {
  if (!_token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(_token)}`;
}

/** 0.1.88 — foto de evidência de um chamado ou notificação (para `<img src>`). */
export function fotoUrl(fotoId: number): string {
  return comToken(apiUrl(`/fotos/${fotoId}`));
}

/** Foto anexada a um chamado ou notificação (0.1.88). */
export interface FotoAnexo {
  id: number; largura: number; altura: number; criado_por: string | null; criado_por_id: number | null;
}

/** URL da imagem de uma página do balancete. `w` (px) devolve versão reduzida. */
export function paginaImgUrl(
  competenciaId: number,
  pagina: number,
  w?: number,
): string {
  const q = w ? `?w=${w}` : "";
  return comToken(apiUrl(`/competencias/${competenciaId}/pagina/${pagina}.png${q}`));
}

/** URL do PDF original (inline). `pagina` posiciona o visualizador (#page=N). */
export function pdfUrl(
  competenciaId: number,
  pagina?: number,
  pdfId?: number,
): string {
  const qs = pdfId != null ? `?pdf_id=${pdfId}` : "";
  const hash = pagina != null ? `#page=${pagina}` : "";
  // O token entra ANTES do #hash (o fragmento não vai ao servidor).
  return comToken(apiUrl(`/competencias/${competenciaId}/pdf${qs}`)) + hash;
}

/** URL do PDF de um documento da Biblioteca (abre inline). */
export function bibliotecaArquivoUrl(docId: number): string {
  return comToken(apiUrl(`/biblioteca/${docId}/arquivo`));
}

/** URL do HTML de uma versão da Apresentação aos condôminos (prévia ou download). */
export function apresentacaoHtmlUrl(apresentacaoId: number, versao: number, baixar = false): string {
  const q = baixar ? "?baixar=true" : "";
  return comToken(apiUrl(`/apresentacoes/${apresentacaoId}/versoes/${versao}/html${q}`));
}

/** URL de download de um CSV do arquivo morto da auditoria. */
export function auditoriaArquivoUrl(nome: string): string {
  return comToken(apiUrl(`/auditoria/arquivos/${encodeURIComponent(nome)}`));
}

/** URL do PDF de um recibo (abre inline para imprimir/baixar). */
export function reciboPdfUrl(reciboId: number): string {
  return comToken(apiUrl(`/recibos/${reciboId}/pdf`));
}

/**
 * URL do "Resumo do Mês" (WhatsApp) para ABRIR em nova aba (baixar/imprimir).
 * `formato`: "pdf" (padrão) ou "png".
 */
export function resumoUrl(competenciaId: number, formato: "pdf" | "png" = "pdf"): string {
  return comToken(apiUrl(`/competencias/${competenciaId}/resumo.arquivo?formato=${formato}`));
}

/**
 * URL da imagem (PNG) do Resumo do Mês para o MORADOR ver no Portal.
 * Passa pelo router `resumo-portal` (só exige login + ser membro do condomínio),
 * ao contrário de `resumoUrl`, que exige permissão de balancete (só gestor).
 */
export function resumoPortalImgUrl(condominioId: number, competenciaId: number): string {
  return comToken(apiUrl(`/condominios/${condominioId}/resumo-portal.png?competencia_id=${competenciaId}`));
}

/** URL do Relatório de Inconsistências (PDF) para ABRIR em nova aba. */
export function relatorioInconsistenciasUrl(competenciaId: number): string {
  return comToken(apiUrl(`/competencias/${competenciaId}/relatorio-inconsistencias.arquivo`));
}

/** URL da Carta de Notificação (PDF) de uma infração — abre inline (nova aba). */
export function cartaInfracaoUrl(notificacaoId: number): string {
  return comToken(apiUrl(`/notificacoes/${notificacaoId}/carta.pdf`));
}

/** URL do boleto (2ª via, 1 página) de uma unidade — abre inline (nova aba). */
export function boletoArquivoUrl(boletoId: number): string {
  return comToken(apiUrl(`/boletos/${boletoId}/arquivo.pdf`));
}

/** URL da ata (PDF) de uma votação encerrada — abre em nova aba. */
export function ataVotacaoUrl(votacaoId: number): string {
  return comToken(apiUrl(`/votacoes/${votacaoId}/ata.pdf`));
}

/** PDF das cobranças de reserva para a administradora (0.1.89) — abre em nova aba. */
export function reservasCobrancasPdfUrl(condominioId: number, ids?: string): string {
  const q = ids ? `?ids=${encodeURIComponent(ids)}` : "";
  return comToken(apiUrl(`/condominios/${condominioId}/reservas/cobrancas.pdf${q}`));
}

/** PDFs da convocação de assembleia (0.1.88) — abrem em nova aba. */
export function assembleiaPdfUrl(
  assembleiaId: number, doc: "edital" | "procuracao" | "lista-presenca" | "comprovante" | "pacote-administradora",
): string {
  return comToken(apiUrl(`/assembleias/${assembleiaId}/${doc}.pdf`));
}

/** URL do documento da procuração (0.1.83) — abre em nova aba. */
export function procuracaoArquivoUrl(procuracaoId: number): string {
  return comToken(apiUrl(`/procuracoes/${procuracaoId}/arquivo`));
}

/** URL do Livro da Portaria (PDF) de um dia — abre em nova aba. */
export function visitasLivroPdfUrl(condominioId: number, data?: string): string {
  const q = data ? `?data=${encodeURIComponent(data)}` : "";
  return comToken(apiUrl(`/condominios/${condominioId}/visitas/livro.pdf${q}`));
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  if (!headers.has("Content-Type") && options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (_token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${_token}`);
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  // Sessão expirou/inválida: limpa o token e avisa o app para voltar ao login.
  if (res.status === 401 && _token) {
    setToken(null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("sindicoapp:sem-sessao"));
    }
  }
  if (!res.ok) {
    // Lê o corpo UMA vez (como texto) e só então tenta interpretar como JSON.
    // Ler json() e depois text() estoura "body stream already read" e esconde
    // a mensagem real do backend.
    let body: unknown;
    const bruto = await res.text().catch(() => "");
    try {
      body = bruto ? JSON.parse(bruto) : "";
    } catch {
      body = bruto;
    }
    // Preferimos a mensagem amigável do backend (FastAPI: {"detail": "..."})
    // em vez de "400 Bad Request". Sem isto, o usuário via só "BAD".
    let mensagem = `${res.status} ${res.statusText}`;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string" && detail.trim()) {
        mensagem = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Erros de validação do FastAPI: [{msg, loc, ...}]
        const msgs = detail
          .map((d) => (d && typeof d === "object" && "msg" in d ? String((d as { msg: unknown }).msg) : null))
          .filter(Boolean);
        if (msgs.length) mensagem = msgs.join("; ");
      }
    } else if (typeof body === "string" && body.trim()) {
      mensagem = body;
    }
    throw new ApiError(res.status, mensagem, body);
  }
  // 204 No Content (DELETE) — nao tem body
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) =>
    request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, file: File, fieldName = "arquivo") => {
    const fd = new FormData();
    fd.append(fieldName, file);
    return request<T>(path, { method: "POST", body: fd });
  },
  // Multipart com arquivo + campos (ex.: Biblioteca: PDF + metadados).
  postForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),
  // Multipart via PUT (ex.: trocar o logo do condomínio).
  putForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "PUT", body: form }),
};

// ============= Types =============
/** 0.1.86 — assinatura do serviço por condomínio (substitui a licença por instalação). */
export type NivelAssinatura = "em_dia" | "a_vencer" | "vencida" | "sem_data";

export interface AssinaturaItem {
  condominio_id: number;
  nome: string;
  vence_em: string | null;          // "AAAA-MM-DD"
  dias_restantes: number | null;    // negativo = dias de atraso
  nivel: NivelAssinatura;
}

export interface AssinaturasResp {
  itens: AssinaturaItem[];
  resumo: {
    nivel: NivelAssinatura;
    total: number;
    vencidas: number;
    a_vencer: number;
    em_dia: number;
    sem_data: number;
    proximo_vencimento: string | null;
    proximo_dias: number | null;
  };
  aviso_dias: number;
  sou_dono: boolean;
}

/** Cores do selo/etiqueta da assinatura (verde, amarelo, vermelho, cinza). */
export const COR_ASSINATURA: Record<NivelAssinatura, string> = {
  em_dia: "text-emerald-700 bg-emerald-50 border-emerald-200",
  a_vencer: "text-amber-800 bg-amber-50 border-amber-300",
  vencida: "text-red-700 bg-red-50 border-red-300",
  sem_data: "text-slate-600 bg-slate-100 border-slate-200",
};

/** "AAAA-MM-DD" → "DD/MM/AAAA" (sem passar por Date: evita o deslize de fuso). */
export function dataBR(iso?: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

/** Texto curto da situação de UMA assinatura. */
export function textoAssinatura(i: Pick<AssinaturaItem, "nivel" | "vence_em" | "dias_restantes">): string {
  const d = i.dias_restantes ?? 0;
  switch (i.nivel) {
    case "vencida":
      return `Vencida há ${-d} dia${-d === 1 ? "" : "s"} (${dataBR(i.vence_em)})`;
    case "a_vencer":
      return d === 0 ? `Vence HOJE (${dataBR(i.vence_em)})`
        : `Vence em ${d} dia${d === 1 ? "" : "s"} (${dataBR(i.vence_em)})`;
    case "em_dia":
      return `Em dia até ${dataBR(i.vence_em)}`;
    default:
      return "Sem data registrada";
  }
}

export interface CondominioListItem {
  id: number;
  nome: string;
  nome_pasta: string;
  numero_unidades: number;
  sindico_nome: string | null;
}

export interface ContaContabil {
  id: number;
  codigo: string;
  nome: string;
  tipo: string;
  ordem: number;
  ativa: boolean;
}

export interface Condominio extends CondominioListItem {
  cnpj: string | null;
  endereco: string | null;
  sindico_cpf?: string | null;
  sindico_telefone?: string | null;
  sindico_email?: string | null;
  contas: ContaContabil[];
  /** Bloqueio temporário (0.1.30) — NULL/undefined = liberado. */
  bloqueado_em?: string | null;
  bloqueado_por?: string | null;
  bloqueado_motivo?: string | null;
}

export interface SystemInfo {
  version: string;
  /** Momento (ISO) em que o backend subiu — para saber se reiniciou (Python travado). */
  iniciado_em?: string;
  env: string;
  /** 0.1.81: preenchido só no ambiente de ensaio (ex.: "HOMOLOGACAO"). */
  ambiente_rotulo?: string;
  workspace: string;
  rodape: string;
}

/**
 * Baixa um arquivo de um endpoint POST (JSON no corpo), disparando o download
 * no navegador (backup, planilhas, PDFs). Envia o token de sessão.
 * Retorna a mensagem de erro do backend quando falha (para a tela mostrar).
 */
export async function baixarArquivoPost(
  path: string,
  body: unknown,
  nomePadrao: string,
): Promise<void> {
  const headers = new Headers({ "Content-Type": "application/json" });
  if (_token) headers.set("Authorization", `Bearer ${_token}`);
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j && typeof j === "object" && "detail" in j) msg = String((j as { detail: unknown }).detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }
  // Nome do arquivo vindo do cabeçalho, se houver.
  let nome = nomePadrao;
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/i);
  if (m) nome = m[1];
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
